import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type Alert from "@/interfaces/Alerts/Alert";
import type ConfigurationFileContents from "@/interfaces/Configuration/ConfigurationFileContents";
import type Notification from "@/interfaces/Notifications/Notification";
import type WebLoginAuthenticationTokens from "@/interfaces/WebLogin/WebLoginAuthenticationTokens";
import { AlertType } from "@/enumerations/Alerts/AlertType";
import { NotificationType } from "@/enumerations/Notifications/NotificationType";
import router from "@/router";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications";

const REFRESH_TOKEN_ENDPOINT = "api/customerPortal/v1/webLogin/refreshToken";

let runtimeConfiguration: ConfigurationFileContents | undefined;
let refreshRequest:
  | Promise<WebLoginAuthenticationTokens | undefined>
  | undefined;

export const portalHttpClient = axios.create();

// Axios 1.15.0+ rejects header values containing CR or LF and aborts the
// request client-side, so strip them from values sourced from config/account data.
function sanitizeHeaderValue(value: string): string {
  return value.replace(/[\r\n]/g, "");
}

function applyRuntimeConfiguration(requestConfig: AxiosRequestConfig = {}) {
  const authStore = useAuthStore();

  requestConfig.params = requestConfig.params ?? {};
  requestConfig.headers = requestConfig.headers ?? {};

  if (runtimeConfiguration?.publicApiKey) {
    requestConfig.params["api-key"] = runtimeConfiguration.publicApiKey;
  }

  if (runtimeConfiguration?.customerPortalKey) {
    requestConfig.headers["Customer-Portal-Key"] = sanitizeHeaderValue(
      runtimeConfiguration.customerPortalKey
    );
  }

  if (requestConfig.headers["Customer-Id"] === undefined) {
    requestConfig.headers["Customer-Id"] = sanitizeHeaderValue(
      authStore.selectedAccount?.id ?? ""
    );
  }

  requestConfig.headers["Entity-Id"] = sanitizeHeaderValue(
    authStore.selectedAccount?.entityId ?? ""
  );

  if (authStore.tokens?.jsonWebToken) {
    requestConfig.headers.Authorization =
      requestConfig.headers.Authorization ??
      `Bearer ${authStore.tokens.jsonWebToken}`;
  }

  return requestConfig;
}

function buildRefreshRequestConfiguration(
  tokens: WebLoginAuthenticationTokens
): AxiosRequestConfig {
  const refreshRequestConfig = applyRuntimeConfiguration({
    method: "post",
    url: REFRESH_TOKEN_ENDPOINT,
    // The refresh request is sent through the bare axios instance (to bypass
    // the 401 interceptor), which does not inherit portalHttpClient's baseURL —
    // without it the request would hit the portal origin instead of the API.
    baseURL: portalHttpClient.defaults.baseURL,
    data: {
      jsonWebToken: tokens.jsonWebToken,
      refreshToken: tokens.refreshToken,
    },
  });

  delete refreshRequestConfig.headers?.Authorization;

  return refreshRequestConfig;
}

async function redirectToLoginWithSessionExpiredAlert() {
  const authStore = useAuthStore();
  const alertsStore = useAlertsStore();

  authStore.clearState();
  alertsStore.setAlert({
    headerMessage: "Session ended due to inactivity",
    bodyMessage: "",
    type: AlertType.Information,
  } as Alert);

  if (router.currentRoute.value.name !== "login") {
    await router.push("/login");
  }
}

async function refreshAuthenticationTokens() {
  const authStore = useAuthStore();

  if (!authStore.tokens) {
    return undefined;
  }

  const response = await axios(buildRefreshRequestConfiguration(authStore.tokens));
  return response.data as WebLoginAuthenticationTokens;
}

export function initializePortalHttpClient(
  configurationContents: ConfigurationFileContents
) {
  runtimeConfiguration = configurationContents;

  if (import.meta.env.PROD && configurationContents.publicApiBaseUrl) {
    portalHttpClient.defaults.baseURL = configurationContents.publicApiBaseUrl;
  } else {
    delete portalHttpClient.defaults.baseURL;
  }
}

portalHttpClient.interceptors.request.use(
  (requestConfig: InternalAxiosRequestConfig) =>
    applyRuntimeConfiguration(requestConfig) as InternalAxiosRequestConfig
);

portalHttpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const authStore = useAuthStore();
    const alertsStore = useAlertsStore();
    const notificationsStore = useNotificationsStore();
    const requestConfig = error.config;

    if (error.response?.status === 422) {
      return error.response;
    }

    if (error.response?.status === 404) {
      notificationsStore.setNotification({
        message: "The requested resource was not found",
        type: NotificationType.Error,
      } as Notification);

      return Promise.reject(error);
    }

    if (requestConfig?.url?.includes(REFRESH_TOKEN_ENDPOINT)) {
      await redirectToLoginWithSessionExpiredAlert();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && authStore.tokens && requestConfig) {
      try {
        refreshRequest =
          refreshRequest ??
          refreshAuthenticationTokens().finally(() => {
            refreshRequest = undefined;
          });

        const refreshedTokens = await refreshRequest;

        if (!refreshedTokens) {
          await redirectToLoginWithSessionExpiredAlert();
          return Promise.reject(error);
        }

        authStore.setWebTokens(refreshedTokens);

        requestConfig.headers = requestConfig.headers ?? {};
        requestConfig.headers.Authorization = `Bearer ${refreshedTokens.jsonWebToken}`;

        return portalHttpClient(requestConfig);
      } catch {
        await redirectToLoginWithSessionExpiredAlert();
        return Promise.reject(error);
      }
    }

    alertsStore.setAlert({
      headerMessage: error.message || "Request failed",
      bodyMessage: "",
      type: AlertType.Error,
    } as Alert);

    return Promise.reject(error);
  }
);
